<li class="list-inline-item reaction-control position-relative" (mouseleave)="onControlMouseLeave()">
  <div class="reaction-control__main" (mouseenter)="onHoverOpenReactionBar()">
    <!--
      No tooltip: hovering already opens the palette, so a tooltip would race it, and the one
      it used to show ("Like this comment") describes a click that no longer reacts.
      No `aria-pressed` either — this button has no pressed state now, it opens a listbox.
    -->
    <button
      type="button"
      class="btn action-pill reaction-control__button"
      aria-label="Add a reaction"
      [attr.aria-expanded]="reactionBarOpen"
      aria-haspopup="true"
      (click)="onMainClick($event)"
      (keydown)="onMainKeydown($event)"
      (pointerdown)="onPointerDown($event)"
      (pointerup)="onPointerUp()"
      (pointerleave)="onPointerLeave()"
      (pointercancel)="onPointerUp()"
    >
      <!--
        The Slack-style "add a reaction" affordance: an OUTLINE smiley with a plus, in the same
        pill the count chips wear, so the row reads as one strip rather than a toolbar icon
        followed by chips. Outline, not a colour emoji — a filled face reads as a reaction
        someone already left rather than a button offering them.
      -->
      <fa-icon class="reaction-control__add-face" [icon]="smileIcon" aria-hidden="true"></fa-icon>
      <fa-icon class="reaction-control__add-plus" icon="plus" aria-hidden="true"></fa-icon>
      <span class="sr-only">Add a reaction</span>
    </button>

    @if (reactionBarOpen) {
      <div
        class="reaction-control__bar"
        [class.reaction-control__bar--right]="barAlignRight"
        role="listbox"
        aria-label="Choose a reaction"
        (keydown)="onBarKeydown($event)"
        (mouseenter)="onHoverOpenReactionBar()"
      >
        @for (type of reactionTypes; track type; let idx = $index) {
          <button
            type="button"
            class="reaction-control__option"
            [class.selected]="myReactionSet[type]"
            [class.focused]="focusedReactionIndex === idx"
            role="option"
            [attr.aria-selected]="myReactionSet[type]"
            [attr.aria-label]="reactionLabels[type]"
            [attr.tabindex]="focusedReactionIndex === idx ? 0 : -1"
            (click)="selectReaction(type); $event.stopPropagation()"
            (mouseenter)="focusedReactionIndex = idx"
          >
            <span aria-hidden="true">{{ reactionEmoji[type] }}</span>
          </button>
        }
      </div>
    }
  </div>
</li>

@for (item of reactionCounts; track item.type) {
  <li class="list-inline-item position-relative reaction-control__count-item" (mouseenter)="showReactors(item.type)" (mouseleave)="scheduleHideReactors()">
    <div
      class="action-pill reaction-control__count"
      [class.action-pill--mine]="myReactionSet[item.type]"
      role="button"
      tabindex="0"
      [attr.aria-label]="reactionLabels[item.type] + ' ' + item.count"
      [attr.aria-expanded]="shouldShowReactorsList && activeReactorType === item.type"
      (click)="onCountClick(item.type, $event)"
      (keydown.enter)="onCountClick(item.type, $event)"
      (keydown.space)="onCountClick(item.type, $event); $event.preventDefault()"
      (focus)="showReactors(item.type)"
      (blur)="scheduleHideReactors()"
    >
      <span aria-hidden="true">{{ item.emoji }}</span>
      <span>{{ item.count }}</span>
    </div>

    @if (shouldShowReactorsList && activeReactorType === item.type && activeReactorUserIds.length) {
      <div class="like-bubble-container reaction-control__reactors" [class.reaction-control__reactors--right]="reactorsAlignRight" (mouseenter)="showReactors(item.type)">
        @if (activeHoveringReactorName) {
          <div class="reaction-control__reactor-name">
            <span class="c-white">{{ activeHoveringReactorName }}</span>
          </div>
        }
        <ul class="like-bubble-background">
          @for (id of activeReactorUserIds; track id) {
            <!-- Reactors without a resolvable photo get the same initials-on-tint
                 fallback comment.component uses for post authors, rather than an
                 empty bubble from a background-image that 404s. -->
            <li
              class="like-bubble"
              [class.like-bubble-initials]="!reactorHasPhoto[id]"
              role="link"
              tabindex="0"
              [ngStyle]="reactorHasPhoto[id] ? reactorPhotoStyles[id] : { 'background-color': reactorColors[id] }"
              (mouseenter)="reactorMouseEnter(id)"
              (mouseleave)="reactorMouseLeave()"
              (focus)="reactorMouseEnter(id)"
              (click)="onViewProfile(id)"
              (keydown.enter)="onViewProfile(id)"
            >
              @if (!reactorHasPhoto[id]) {
                <span aria-hidden="true">{{ reactorInitials[id] }}</span>
              }
              <span class="sr-only">go to {{ activeHoveringReactorNameList[id] ?? "user" }} profile</span>
            </li>
          }
        </ul>
      </div>
    }
  </li>
}
